import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Rx';
import { ConfirmComponent } from './confirm.component';
import { MessageComponent } from './message.component';
import { DialogService, DialogCloseResult } from '@progress/kendo-angular-dialog';
import {DatePipe} from '@angular/common';
import { Day, prevDayOfWeek, firstDayOfMonth, addDays, lastDayOfMonth, createDate, addYears, addMonths  } from '@progress/kendo-date-math';
import {ChooseImageComponent} from "./choose-image.component";
import {Params, Router} from "@angular/router";
import {divide, multiply} from "@progress/kendo-angular-inputs/dist/es2015/common/math";
import {ReportParam} from "./models/ReportParam";
import {ReportPreviewComponent} from "./report-preview.component";

@Injectable()
export class SharedService {
    private apiUrlBaseItem: string;

    constructor(private dialogService: DialogService,
                private router: Router,
                public datepipe: DatePipe) {
    }

    public navigate(path: string, params: Params = null) {
        this.router.navigate([`/auth/${path}`],
            {
                queryParams: params,
                skipLocationChange: true
            });
    }

    public confirm = (message: string) : Observable<boolean> => {
        return Observable.create(observer => {
            const dialogRef = this.dialogService.open({
                content: ConfirmComponent
            });

            const dialogContent: ConfirmComponent = dialogRef.content.instance;
            dialogContent.dialog = dialogRef;
            dialogContent.message = message;

            dialogRef.result
                .subscribe((result: any) => {
                    if (result instanceof DialogCloseResult) {
                        observer.next(false);
                    }
                    else
                        observer.next(result);
                    observer.complete();
                });
        });
    };

    public message(message: string)  {
        const dialogRef = this.dialogService.open({
            content: MessageComponent
        });

        const dialogContent: MessageComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.message = message;

        dialogRef.result.subscribe();
    }

    public chooseImage = (image: string, maxWidth: number, aspectRation: number) : Observable<string> => {
        return Observable.create(observer => {
            const dialogRef = this.dialogService.open({
                content: ChooseImageComponent
            });

            const dialogContent: ChooseImageComponent = dialogRef.content.instance;
            dialogContent.dialog = dialogRef;
            dialogContent.aspectRatio = aspectRation;
            dialogContent.maxWidth = maxWidth;
            dialogContent.imageSource = image;

            dialogContent.chosenImage
                .subscribe((result: string) => {
                    observer.next(result);
                    observer.complete();
                });
        });
    };

    public round(number, precision) {
        if (precision <= 0) {
            return Math.round(number);
        }

        const tho = 10 ** precision;

        return Math.round(number * tho) / tho;
    };

    public roundToCents(number: number, cents: number) {
        let centsDivider = divide(100, cents);
        return divide(this.round(multiply(number, centsDivider), 0), centsDivider);
    };

    showPrintPreview(reportName: string, reportParams: ReportParam[]) {
        const dialogRef = this.dialogService.open({
            content: ReportPreviewComponent
        });

        const dialogContent: ReportPreviewComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.reportName = reportName;
        dialogContent.reportParams = reportParams;
    }

    getAge(birthdate: Date) {
        let timeDiff = Math.abs(Date.now() - birthdate.getTime());
        return Math.floor(timeDiff / (1000 * 3600 * 24) / 365);
    }

    formatDate(date: Date, format: string = 'dd-MM-yyyy') {
        if (date) {
            return this.datepipe.transform(date, format);
        }

        return '';
    }

    getCurrentYear() {
        return parseInt(this.datepipe.transform(this.getCurrentDate(), 'yyyy'));
    }

    getYearFromDate(date: Date) {
        return parseInt(this.datepipe.transform(date, 'yyyy'));
    }

    getCurrentWeek() {
        return this.getWeekNumber(this.getCurrentDate());
    }

    getCurrentQuarter() {
        if (this.getCurrentDate().getMonth() <= 2) return 1;
        else if (
            this.getCurrentDate().getMonth() > 2 &&
            this.getCurrentDate().getMonth() <= 5
        )
            return 2;
        else if (
            this.getCurrentDate().getMonth() > 5 &&
            this.getCurrentDate().getMonth() <= 8
        )
            return 3;
        else if (
            this.getCurrentDate().getMonth() > 8 &&
            this.getCurrentDate().getMonth() <= 11
        )
            return 4;
    }

    getCurrentDate() {
        return new Date(Date.now());
    }

    createDateTime(year: number, month: number, day: number, hour: number, minute: number, second: number): Date {
        return createDate(year, month, day, hour, minute, second);
    }

    getLocalTimeString() {
        return this.formatDate(new Date(Date.now()), 'HH:mm');
    }

    getCurrentDateOnly() {
        return new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    }

    getDateOnly(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    getWeekNumber(d: Date): number {
        // Copy date so don't modify original
        d = new Date(+d);
        d.setHours(0, 0, 0);
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        // Get first day of year
        let yearStart = new Date(d.getFullYear(), 0, 1);
        // Calculate full weeks to nearest Thursday
        // Return array of year and week number
        return Math.ceil(
            ((d.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7
        );
    }

    getFirstDateOfWeek(year: number, weekOfYear: number): Date {
        let jan1 = new Date(year, 0, 1);
        let daysOffset = 4 - jan1.getDay();

        let firstThursday = this.addDaysToDate(jan1, daysOffset);
        let firstWeek = this.getWeekNumber(firstThursday);

        let weekNum = weekOfYear;
        if (firstWeek <= 1)
        {
            weekNum -= 1;
        }
        let result = this.addDaysToDate(firstThursday, (weekNum * 7));
        return this.addDaysToDate(result, -3);
    }

    getFirstDayOfWeek(d: Date): Date {
        if (d.getDay() == Day.Monday) return d;

        return prevDayOfWeek(d, Day.Monday);
    }

    getFirstDayOfMonth(d: Date): Date {
        return firstDayOfMonth(d);
    }

    getLastDayOfMonth(d: Date): Date {
        return lastDayOfMonth(d);
    }

    getFirstDayOfYear(d: Date): Date {
        return createDate(d.getFullYear(), 0, 1, 0, 0, 0);
    }

    getLastDayOfYear(d: Date): Date {
        return createDate(d.getFullYear(), 11, 31, 0, 0, 0);
    }

    addDaysToDate(d: Date, days: number): Date {
        return addDays(d, days);
    }

    addMinutesToDate(d: Date, minutes: number): Date {
        let ret = new Date(d);
        ret.setTime(ret.getTime() + minutes * 60000);
        return ret;
    }

    addMonthsToDate(d: Date, months: number): Date {
        return addMonths(d, months);
    }

    addYearsToDate(d: Date, years: number): Date {
        return addYears(d, years);
    }

    getDayOfYear(d: Date) {
        let timestmp = new Date().setFullYear(d.getFullYear(), 0, 1);
        let yearFirstDay = Math.floor(timestmp / 86400000);
        let today = Math.ceil(d.getTime() / 86400000);
        return today - yearFirstDay;
    }

    minutesBetweenDates(dt1: Date, dt2: Date) {
        let diff = (dt2.getTime() - dt1.getTime()) / 1000;
        diff /= 60;
        return Math.abs(Math.round(diff));
    }

    dateTimeToNumber(date: Date): number {
        return this.round(date.getHours() + (date.getMinutes() / 60), 2);
    }

    numberToTimeString(time: number): string {
        if (time === 0)
            return '0:00';

        let hours = Math.floor(time);
        let minutes = (time - hours) * 60;
        let H = Math.floor(hours).toString();
        let M = this.round(minutes, 0).toString();
        if (M.length === 1)
            M = `0${M}`;

        return `${H}:${M}`;
    }

    numberToTimeDate(time: number): Date {
        if (time === 0)
            return new Date(1899,0,1,0,0,0);

        let hours = Math.floor(time);
        let minutes = (time - hours) * 60;
        let H = Math.floor(hours);
        let M = this.round(minutes, 0);

        return new Date(1899,0,1,H,M,0);
    }

    removeColorAlpha(color: string) {
        if (color && color.length >= 9) {
            return '#' + color.substr(3, 6);
        } else return color;
    }

    getCurrentDateToCompare(): number {
        return this.getDateToCompare(new Date(Date.now()));
    }

    getDateToCompare(date: Date): number {
        if (date)
            return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        return null;
    }

    public CalculateColumnWidth(title: string) {
        let re = /\./gi;
        title = title.replace(re, '');
        re = /!/gi;
        title = title.replace(re, '');

        return Math.ceil(title.length * 9);
    }

    public randomNumber(min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    public useEmployees() {
        return (sessionStorage.getItem('useEmployees') && sessionStorage.getItem('useEmployees') == '1');
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/shared/shared.service.ts